import CreateRoleDialog from "../commons/Manage/CreateRole";

/**
 * True only for a full-access ("*") admin.
 *
 * Must be given the RAW acl — `state.login.user.permissions` — not the expanded
 * tree in `state.login.permissions`. `expandPermissionSchema` turns `[{"*": true}]`
 * into the complete permission tree, which is indistinguishable from a role that
 * simply had every box ticked.
 */
export const isGlobalAdmin = (rawPermissions) =>
  Array.isArray(rawPermissions) &&
  rawPermissions.length === 1 &&
  rawPermissions[0]?.["*"] === true;

/**
 * True for a role explicitly marked "search only" for Assistants — such a role
 * lands on a blank Assistants table and reaches accounts only via search.
 *
 * Reads the RAW acl (`state.login.user.permissions`), NOT the expanded tree:
 * a global-admin `[{"*": true}]` and any "select all" role collapse to `*` and
 * carry no per-action key, so they are never treated as restricted. Roles that
 * predate this key have no `assistants` action entry and are unaffected.
 */
export const isAssistantSearchOnly = (rawPermissions) => {
  if (isGlobalAdmin(rawPermissions)) return false;
  const node = Array.isArray(rawPermissions)
    ? rawPermissions.find((p) => p?.label === "assistants")
    : null;
  return (
    Array.isArray(node?.actions) && node.actions.includes("assistantSearchOnly")
  );
};

export const verifyRouteAccess = (acl, route) => {
  if (!acl?.length || acl.find((e) => e.includes(route))) {
    return true;
  }

  return false;
};

export const verifyRouteAccess2 = (permissions, route) => {
  // if (route.to === "/marketplace") {
  //   const permission = permissions?.find((p) => p.label === "marketplace");
  //   return permission ? permission.read : true;
  // }
  if (route.to === "/crm") {
    const permission = permissions?.find((p) => p.label === "dashboard");
    return permission?.actions?.includes("searchProject");
  }
  if (route.to === "/") {
    const permission = permissions?.find((p) => p.label === "dashboard");
    return permission?.read;
  }
  const permission = permissions?.find((p) => "/" + p.label === route.to);
  return permission?.read;
};

export const verifySubRouteAccess = (subroutes, key, accessLevel) => {
  if (accessLevel === "read") {
    return subroutes?.find((e) => e.label === key)?.read;
  } else if (accessLevel === "write") {
    return subroutes?.find((e) => e.label === key)?.write;
  }
  return false;
};

export const verifySubRouteAction = (subroutes, key) => {
  return subroutes?.actions?.includes(key);
};

export const expandPermissionSchema = (
  permissions,
  schema = CreateRoleDialog.permissionTree
) => {
  // If it's global full access, expand all permissions
  if (permissions.length === 1 && permissions[0]["*"] === true) {
    return expandAllPermissions(schema);
  }

  // Create a map of existing permissions by key for quick lookup
  const permissionMap = new Map();
  permissions.forEach((permission) => {
    permissionMap.set(permission.label, permission);
  });

  // Return an array with the same structure as schema
  return schema.map((schemaItem) => {
    const permission = permissionMap.get(schemaItem.key);
    
    if (!permission) {
      // If no permission exists for this schema item, return default structure
      return {
        key: schemaItem.key,
        read: false,
        write: false,
        actions: schemaItem.actions ? [] : undefined,
        subroutes: schemaItem.subroutes
          ? expandPermissionSchema([], schemaItem.subroutes)
          : undefined,
      };
    }

    // If this permission has *: true, expand it fully
    if (permission["*"] === true) {
      return expandPermissionItem(schemaItem);
    }

    // If it has subroutes, expand them recursively
    if (permission.subroutes && schemaItem.subroutes) {
      const expandedSubroutes = expandPermissionSchema(
        permission.subroutes,
        schemaItem.subroutes
      );
      return {
        ...permission,
        key: schemaItem.key,
        subroutes: expandedSubroutes,
      };
    }

    // Initialize actions array if not present
    const expanded = { 
      ...permission,
      key: schemaItem.key,
    };
    if (schemaItem.actions && !expanded.actions) {
      expanded.actions = [];
    }
    return expanded;
  });
};

// Helper to expand a single permission item with *: true
const expandPermissionItem = (schemaItem) => {
  const expanded = {
    key: schemaItem.key,
    label: schemaItem.key,
    read: schemaItem.read !== undefined ? true : undefined,
    write: schemaItem.write !== undefined ? true : undefined,
  };

  // Add actions if they exist
  if (schemaItem.actions && schemaItem.actions.length > 0) {
    expanded.actions = schemaItem.actions.map((action) => action.key);
  }

  // Add subroutes if they exist
  if (schemaItem.subroutes && schemaItem.subroutes.length > 0) {
    expanded.subroutes = schemaItem.subroutes.map((subItem) =>
      expandPermissionItem(subItem)
    );
  }

  // Remove undefined values
  Object.keys(expanded).forEach((key) => {
    if (expanded[key] === undefined) {
      delete expanded[key];
    }
  });

  return expanded;
};

// Helper to expand all permissions when global *: true is used
const expandAllPermissions = (schema) => {
  return schema.map((item) => ({
    ...expandPermissionItem(item),
    key: item.key,
  }));
};
